import { PaginatedResponse, useApi } from '@/api';
import { EtherscanLink } from '@/components';
import { tokensMapping } from '@/utils/tokens';
import {
  CryptoIcon,
  CurrencyValue,
  DataTable,
  Flex,
  RelativeDateTime,
  TableSkeleton,
  useTableProps
} from '@blockanalitica/ui';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';

type Event = {
  datetime: string;
  order_index: string;
  tx_hash: string;
  wallet_address: string;
  event: string;
  amount: string;
  address: string;
};

type HolderEventProps = {
  walletAddress: string;
};

export default function HolderActivityPage({
  walletAddress
}: HolderEventProps) {
  const { pagination, sorting } = useTableProps({
    pageSize: 10,
    initialSorting: '-order_index'
  });
  const [data, error] = useApi<PaginatedResponse<Event[]>>({
    url: `/sparkstar/staking/holders/${walletAddress}/events/`,
    queryParams: {
      p: pagination.currentPage,
      p_size: pagination.pageSize,
      order: sorting.currentSorting
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <TableSkeleton rows={10} columns={6} />;
  }

  const columns: TableColumn[] = [
    {
      header: 'Event',
      getCell: (row) => row.event
    },
    {
      header: (
        <Flex display="flex" flexDirection="row" alignItems="center" gap="3xs">
          Amount
          <CryptoIcon
            name={'SPK'}
            address={tokensMapping('SPK').address}
            size="0"
          />
        </Flex>
      ),
      getCell: (row) => <CurrencyValue value={row.amount} />,
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'amount'
    },
    {
      header: 'Time',
      getCell: (row) => (
        <Flex gap="xs" color="muted">
          <RelativeDateTime value={row.datetime} />
          <EtherscanLink value={row.tx_hash} network={'ethereum'} />
        </Flex>
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'datetime'
    }
  ];
  return (
    <DataTable
      rows={data.results}
      columns={columns}
      getKey={(row) => `${row.order_index}`}
      sorting={sorting}
      pagination={{
        ...pagination,
        totalRecords: data.count
      }}
    />
  );
}
